/**
 * Dados do filtro "Somente Amazônia Legal" (thread principal).
 *
 * - `municipios_codigos.json` (lista de códigos IBGE; só os focos usam).
 * - Polígono `amazonia_legal`: reaproveita o carregador do módulo de vetores
 *   (`ensureDataSource`), que já baixa o `manifest.json` e esse arquivo para a
 *   camada "Estados e Amazônia Legal" (ligada por padrão) — sem download nem
 *   leitura em dobro. As geometrias já vêm na projeção do GOES, então o teste
 *   ponto-em-polígono é feito nas coordenadas do mapa dos focos.
 */
import type Geometry from 'ol/geom/Geometry';
import { ensureDataSource } from '../vectors/loader';
import { PolygonIndex, parseMunicipioCodes } from './amazonia';

const dataUrl = (file: string) => `${import.meta.env.BASE_URL ?? '/'}data/${file}`;

let codesPromise: Promise<Set<number> | null> | null = null;

/** Lista de municípios da Amazônia Legal (ou null se o arquivo ainda não existe). */
export function loadMunicipioCodes(): Promise<Set<number> | null> {
  codesPromise ??= fetchJson(dataUrl('municipios_codigos.json'))
    .then(parseMunicipioCodes)
    .catch(() => null)
    .then((codes) => {
      if (!codes) codesPromise = null; // tenta de novo na próxima carga
      return codes;
    });
  return codesPromise;
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const text = await res.text();
  // O servidor de desenvolvimento pode devolver o index.html para arquivos ausentes.
  if (/^\s*</.test(text)) throw new Error('não é JSON');
  return JSON.parse(text);
}

/** Anéis (coordenadas do mapa) de polígonos/multipolígonos do OpenLayers. */
export function ringsFromGeometry(geometry: Geometry | null | undefined, out: number[][][] = []): number[][][] {
  if (!geometry) return out;
  const type = geometry.getType();
  if (type === 'Polygon') {
    out.push(...(geometry as unknown as { getCoordinates(): number[][][] }).getCoordinates());
  } else if (type === 'MultiPolygon') {
    for (const p of (geometry as unknown as { getCoordinates(): number[][][][] }).getCoordinates()) out.push(...p);
  } else if (type === 'GeometryCollection') {
    for (const g of (geometry as unknown as { getGeometries(): Geometry[] }).getGeometries()) ringsFromGeometry(g, out);
  }
  return out;
}

let polygonPromise: Promise<PolygonIndex | null> | null = null;

/** Polígono da Amazônia Legal indexado, nas coordenadas do mapa (ou null). */
export function loadAmazoniaPolygon(): Promise<PolygonIndex | null> {
  polygonPromise ??= ensureDataSource('amazonia_legal')
    .then((source) => {
      const rings: number[][][] = [];
      for (const f of source?.getFeatures() ?? []) ringsFromGeometry(f.getGeometry(), rings);
      const valid = rings.filter((r) => r.length >= 3 && r.every((c) => Number.isFinite(c[0]) && Number.isFinite(c[1])));
      return valid.length ? new PolygonIndex(valid) : null;
    })
    .catch(() => null)
    .then((index) => {
      if (!index) polygonPromise = null;
      return index;
    });
  return polygonPromise;
}
